import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useFirmReportData } from '../../../../../api/coreData'
import { useDenaliFilter } from '../../filters/DenaliFilterContextProvider'
import TreeMap from '../../../../charts/echarts/TreeMap'
import ErrorBoundary from '../../../../molecules/ErrorBoundary'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])

  return {
    valueFormatter
  }
}

function groupData (data, indexBy, valueAccessor) {
  // Recursive function: at each level, group the items by the first property in indexBy
  const [currentKey, ...restKeys] = indexBy

  const groups = data.reduce((acc, item) => {
    const key = item[currentKey]
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  return Object.keys(groups).map(key => {
    const items = groups[key]
    if (restKeys.length === 0) {
      // Leaf node: use the first item in the group for the aggregated value
      return { name: key, value: items[0][valueAccessor] }
    }
    const children = groupData(items, restKeys, valueAccessor)
    const value = children.reduce((acc, item) => acc + Number(item.value), 0)
    // Recursive grouping for children
    return { name: key, value, children }
  })
}

function DenaliTreeMapEChart ({
  reportId,
  baseQuery,
  indexBy,
  valueAccessor,
  valueFormat,
  height,
  ...chartProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(() => {
    return {
      ...baseQuery,
      membershipFilter,
      filters: {
        ...(filter || {}),
        ...(baseQuery?.filters || {})
      },
      reportId
    }
  }, [reportId, baseQuery, filter, membershipFilter])

  const { data: report, isLoading } = useFirmReportData(query)
  const chartData = useMemo(() => {
    if (!report) return {}

    return groupData(report.data, indexBy, valueAccessor)
  }, [report, indexBy, valueAccessor])
  const { valueFormatter } = useFormatters(valueFormat)

  if (isLoading) return null

  return (
    <ErrorBoundary name='Tree Map'>
      <TreeMap
        height={height}
        data={chartData}
        valueFormat={valueFormatter}
        identity='name'
        value='value'
        {...chartProps}
      />
    </ErrorBoundary>
  )
}

DenaliTreeMapEChart.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  indexBy: PropTypes.array,
  valueAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

DenaliTreeMapEChart.defaultProps = {
  valueFormat: 'marketValue',
  height: 300
}

export default DenaliTreeMapEChart
